import { useSelector, useDispatch } from "react-redux";
import { setUser, initialState } from "store/auth/userSlice";
import { useNavigate } from "react-router-dom";
import appConfig from "configs/app.config";
import {
  logIn,
  createNewUser,
  sendLoginOtp,
  verifyLoginOtp,
} from "services/authentication";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import useQuery from "./useQuery";
import {
  extractOtpSessionToken,
  getApiErrorMessage,
  decodeJwtPayload,
} from "utils/authOtpUtils";
import { markAdminLoginCelebrationPendingIfFirst } from "utils/adminLoginCelebration";
import { resolveUserPermissionsForSession } from "utils/userPermissions";

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const { token, signedIn } = useSelector((state) => state.auth.session);

  /** Shared success path for password login and OTP verify (expects JWT payload on resp.data). */
  const finalizeLoginResponse = async (resp) => {
    const tokenPayload = resp?.data;
    const accessToken = tokenPayload?.access;

    if (!accessToken) {
      return {
        status: "failed",
        message: "Invalid login response. Please try again.",
      };
    }

    localStorage.setItem("accessToken", accessToken);
    dispatch(onSignInSuccess(tokenPayload));

    const user = tokenPayload.user || null;

    const jwtPayload = decodeJwtPayload(accessToken);
    const isSuperuser =
      user?.is_superuser ||
      tokenPayload?.is_superuser ||
      jwtPayload?.is_superuser === true ||
      jwtPayload?.role === "superuser" ||
      false;
    const isStaff = user?.is_staff || tokenPayload?.is_staff || false;
    const designation =
      user?.designation ??
      jwtPayload?.designation ??
      (jwtPayload?.role === "hod"
        ? "hod"
        : jwtPayload?.role === "manager"
          ? "manager"
          : "");

    const permissions = await resolveUserPermissionsForSession({
      user: { ...user, designation },
      accessToken,
      isSuperuser,
    });

    if (user) {
      dispatch(
        setUser({
          ...user,
          permissions: permissions,
          designation,
          is_staff: isStaff,
          is_superuser: isSuperuser,
        })
      );
    }

    const redirectUrl = query.get(REDIRECT_URL_KEY);
    markAdminLoginCelebrationPendingIfFirst();
    navigate(
      redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
    );

    return {
      status: "success",
      message: "Welcome to shipclues",
    };
  };

  const signIn = async (values) => {
    try {
      const resp = await logIn(values);

      if (!resp?.data?.access) {
        return {
          status: "failed",
          message: "Sign in failed. Please check your credentials.",
          errorType: "invalid_password",
        };
      }

      return await finalizeLoginResponse(resp);
    } catch (errors) {
      const raw = errors?.response?.data;
      const msg = getApiErrorMessage(
        errors,
        errors?.message || "Sign in failed."
      );
      let errorType = "unknown";
      if (raw?.email || /email/i.test(msg)) {
        errorType = "invalid_email";
      } else if (/password|credential/i.test(msg)) {
        errorType = "invalid_password";
      }
      return {
        status: "failed",
        message: msg,
        errorType,
      };
    }
  };

  const sendLoginOtpEmail = async (email) => {
    try {
      const trimmed = String(email || "").trim();
      if (!trimmed) {
        return { status: "failed", message: "Please enter your email." };
      }
      const resp = await sendLoginOtp(trimmed);
      const sessionToken = extractOtpSessionToken(resp.data);
      if (!sessionToken) {
        return {
          status: "failed",
          message:
            "Could not start OTP login. Please check your email or try again.",
        };
      }
      return {
        status: "success",
        sessionToken,
        message: resp.data?.message,
      };
    } catch (e) {
      return {
        status: "failed",
        message: getApiErrorMessage(e, "Failed to send OTP."),
      };
    }
  };

  const signInWithOtp = async ({ sessionToken, otp }) => {
    try {
      if (!sessionToken || otp == null || String(otp).trim() === "") {
        return {
          status: "failed",
          message: "Please enter the OTP sent to your email.",
        };
      }
      const resp = await verifyLoginOtp({ token: sessionToken, otp });
      if (!resp?.data?.access) {
        return {
          status: "failed",
          message: getApiErrorMessage(
            { response: { data: resp.data } },
            "Verification failed."
          ),
        };
      }
      return await finalizeLoginResponse(resp);
    } catch (e) {
      return {
        status: "failed",
        message: getApiErrorMessage(e, "Invalid or expired OTP."),
      };
    }
  };

  const signUp = async (values) => {
    try {
      const resp = await createNewUser(values);
      console.log("createNewUser response:", resp);

      if (resp.data && resp.status === 201) {
        dispatch(setUser(resp.data));

        return {
          status: 201,
          message: "User created successfully",
          data: resp.data,
        };
      }

      return {
        status: "failed",
        message: "Unexpected response format",
      };
    } catch (errors) {
      console.error("SignUp error:", errors);
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());
    dispatch(setUser(initialState));
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signInWithOtp,
    sendLoginOtpEmail,
    signUp,
    signOut,
  };
}

export default useAuth;
